import { createContext, useCallback, useEffect, useMemo, useRef } from "react";
import { useExercise } from "hooks";
import useBoolean from "hooks/useBoolean";
import useChartSelections from "components/charting/hooks/useChartSelections";
import AddOneRepMaxDialog from "../../AddOneRepMaxDialog";
import useOneRepMaxEntries from "../../useOneRepMaxEntries";
import useOneRepMaxChartWeightMetric from "../../useOneRepMaxChartWeightMetric";

export interface AddOneRepMaxDialogContextValue {
  openAddOneRepMaxDialog: () => void;
  /** No exercise to add a 1RM to. */
  disabled: boolean;
  /** The entries table's add button, which takes focus when the button that opened the dialog is gone. */
  addButtonRef: React.RefObject<HTMLButtonElement>;
}

export const AddOneRepMaxDialogContext = createContext<AddOneRepMaxDialogContextValue | undefined>(undefined);
AddOneRepMaxDialogContext.displayName = "AddOneRepMaxDialogContext";

interface AddOneRepMaxDialogProviderProps {
  children?: React.ReactNode;
}

type OpenAddOneRepMaxDialogProps = Omit<React.ComponentProps<typeof AddOneRepMaxDialog>, "open" | "currentOneRepMax">;

// Reads the entries only while open, so the velocity source never requests them.
function OpenAddOneRepMaxDialog(props: OpenAddOneRepMaxDialogProps) {
  const entries = useOneRepMaxEntries();

  return <AddOneRepMaxDialog open currentOneRepMax={entries[0]?.weight} {...props} />;
}

// Lives above the chart so the dialog survives the no-data state unmounting once the first 1RM is added.
function AddOneRepMaxDialogProvider(props: AddOneRepMaxDialogProviderProps) {
  const { sources, measures } = useChartSelections();
  const athleteId = sources[0]?.id ?? "";
  const exercise = useExercise(measures[0]?.id ?? "");
  const weightMetric = useOneRepMaxChartWeightMetric();

  const { value: open, setTrue: setOpen, setFalse: closeAddOneRepMaxDialog } = useBoolean();
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const openAddOneRepMaxDialog = useCallback(() => {
    triggerRef.current = document.activeElement;
    setOpen();
  }, [setOpen]);

  // Saving the first 1RM replaces the no-data state, and the button that opened the dialog, before it closes.
  useEffect(() => {
    if (open || !triggerRef.current) return;
    const trigger = triggerRef.current;
    triggerRef.current = null;
    if (!trigger.isConnected) addButtonRef.current?.focus();
  }, [open]);

  const value = useMemo<AddOneRepMaxDialogContextValue>(
    () => ({ openAddOneRepMaxDialog, disabled: !exercise, addButtonRef }),
    [exercise, openAddOneRepMaxDialog]
  );

  return (
    <AddOneRepMaxDialogContext.Provider value={value}>
      {props.children}
      {/* Mounted only while open so each opening starts a fresh form, dated today. */}
      {open ? (
        <OpenAddOneRepMaxDialog
          onClose={closeAddOneRepMaxDialog}
          athleteId={athleteId}
          exercise={exercise}
          weightMetric={weightMetric}
        />
      ) : null}
    </AddOneRepMaxDialogContext.Provider>
  );
}

export default AddOneRepMaxDialogProvider;
